import axios, { AxiosRequestConfig, AxiosError } from 'axios'

/**
 * Framework-agnostic API client — the React landing point for the ~27 axios
 * service files lifted from the Vue app's `services/` layer (~95% portable).
 *
 * Ported services should import `makeAuthenticatedRequest` here instead of the
 * old Vue api/client.js. Same auth/tenant behaviour, no Vue dependency.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second timeout
})

// Track if we're already showing a server error to prevent multiple redirects
let isShowingServerError = false

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  // Debug: log the actual request URL and params
  console.log('[API] Request:', config.method?.toUpperCase(), config.url, 'params:', config.params)
  return config
})

api.interceptors.response.use(
  (res) => {
    // Reset server error flag on successful response
    isShowingServerError = false
    return res
  },
  (err: AxiosError) => {
    const status = err?.response?.status

    // Handle 401 Unauthorized
    if (status === 401) {
      // Clear auth state and redirect to login
      localStorage.removeItem('access_token')
      localStorage.removeItem('bheem-auth')
      // Redirect to login (avoid redirect loop if already on login page)
      if (!window.location.pathname.includes('/auth/')) {
        window.location.href = '/auth/login'
      }
      return Promise.reject(err)
    }

    // Handle 5xx server errors (502, 503, 504, 500)
    if (status && status >= 500 && !isShowingServerError) {
      isShowingServerError = true
      console.error(`[API] Server error ${status}:`, err.message)

      // Dispatch a custom event for UI components to handle
      const serverErrorEvent = new CustomEvent('api:serverError', {
        detail: {
          status,
          message: err.message,
          url: err.config?.url,
        },
      })
      window.dispatchEvent(serverErrorEvent)

      // Reset flag after a delay to allow retry
      setTimeout(() => {
        isShowingServerError = false
      }, 5000)
    }

    // Handle network errors (no response - could be CORS, network down, etc.)
    if (!err.response && err.code === 'ERR_NETWORK') {
      console.error('[API] Network error:', err.message)
      const networkErrorEvent = new CustomEvent('api:networkError', {
        detail: {
          message: 'Unable to connect to the server. Please check your connection.',
        },
      })
      window.dispatchEvent(networkErrorEvent)
    }

    // Handle timeout errors
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      console.error('[API] Timeout error:', err.message)
      const timeoutEvent = new CustomEvent('api:timeout', {
        detail: {
          message: 'Request timed out. Please try again.',
          url: err.config?.url,
        },
      })
      window.dispatchEvent(timeoutEvent)
    }

    return Promise.reject(err)
  },
)

export async function makeAuthenticatedRequest<T = unknown>(
  config: AxiosRequestConfig,
): Promise<T> {
  const res = await api.request<T>(config)
  return res.data
}

export default api
